import React, { useState } from "react";
import GenericAreaChart from "../../components/charts/GenericAreaChart";
import { useUser } from "../../context/UserContext";
import DeviceLogsPanel from "../maintenance/devices/DeviceLogsPanel";
import { Activity } from "lucide-react";

interface Connector {
  id: string;
  name: string;
  socket_type: string;
  status: string;
}

interface Charger {
  id: string;
  name: string;
  status: string;
  type: string;
  power_kw: number;
  connectors: Connector[];
  currentSession?: {
    user: string;
    duration: string;
    energy: string;
  };
}

interface UtilizationData {
  date: string;
  utilization_percent: number;
}

interface ChargersResponse {
  chargers: Charger[];
  utilization_chart: UtilizationData[];
}

interface ChargersTabProps {
  data: ChargersResponse;
}

const ChargersTab: React.FC<ChargersTabProps> = ({ data }) => {
  const { user } = useUser();
  const [logsChargerId, setLogsChargerId] = useState<string | null>(null);

  return (
    <div className="chargers-tab">
      <div className="chargers-grid">
        {data?.chargers?.map((charger, idx) => (
          <div key={idx} className="charger-card">
            <div className="charger-header">
              <h4>{charger.name}</h4>
              <span className={`charger-status-badge ${charger.status.toLowerCase()}`}>
                {charger.status.charAt(0).toUpperCase() + charger.status.slice(1)}
              </span>
            </div>

            <div className="charger-specs">
              <div className="spec-row">
                <div className="spec-item">
                  <span className="spec-label">Type</span>
                  <span className="spec-value">{charger.type}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Power</span>
                  <span className="spec-value">{charger.power_kw} kW</span>
                </div>
              </div>
              <div className="spec-row">
                <div className="spec-item">
                  <span className="spec-label">Connectors</span>
                  <span className="spec-value">
                    {charger.connectors.map((c) => c.socket_type).join(", ")}
                  </span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Health</span>
                  <span className="spec-value">100%</span>
                </div>
              </div>
            </div>

            {charger.currentSession && (
              <div className="current-session">
                <div className="session-label">Current session</div>
                <div className="session-user">{charger.currentSession.user}</div>
                <div className="session-stats">Duration: {charger.currentSession.duration}</div>
                <div className="session-stats">Energy: {charger.currentSession.energy}</div>
              </div>
            )}

            <div className="charger-actions">
              <button
                className={`settings-btn${logsChargerId === charger.id ? " active" : ""}`}
                onClick={() => setLogsChargerId(logsChargerId === charger.id ? null : charger.id)}
                style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}
              >
                <Activity size={14} /> {logsChargerId === charger.id ? "Hide Logs" : "Live Logs"}
              </button>
            </div>

            {logsChargerId === charger.id && user?.org_id && (
              <div style={{ marginTop: "8px" }}>
                <DeviceLogsPanel
                  deviceId={parseInt(charger.id)}
                  uniqueName={charger.name}
                  tenantId={user.org_id}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <GenericAreaChart
        data={data?.utilization_chart?.map((item) => ({
          date: item.date,
          utilization: item.utilization_percent,
        }))}
        title="Charger utilization"
        xAxisKey="date"
        yAxisKey="utilization"
        color="#8b5cf6"
        height={300}
        yAxisFormatter={(value) => `${value}%`}
        fullWidth={true}
      />
    </div>
  );
};

export default ChargersTab;
